import { Ioc } from "@core/ioc/ioc";
import type { IStorageModel } from "./IStorageModel";
import { STORAGE_MODEL } from "./IStorageModel";

/**
 * `profilePicCache.ts` — module-scoped cache shared by every surface that shows
 * a user's picture: the projects-grid avatar, the Profile Settings dialog, the
 * participants roster, and comment / reply authors.
 *
 * Port of Unity's `CustomerModel.m_userProfilePics` (`CustomerModel.cs:434`) plus
 * the `AsyncNamedLock m_loadUserProfilePicLock` (`:703`) that de-duplicates
 * concurrent loads of the same user — the web equivalent of that lock is the
 * `inflight` map below. Unity caches ONE texture per userId for the lifetime of
 * the session and clears it on sign-out / account switch
 * (`ClearUserProfilePicCache`, `:705`); this mirrors that.
 *
 * Shaped after the sibling `thumbnailCache.ts`, including its most important
 * property: **cached `blob:` URLs deliberately outlive consumer scope.** A
 * comment list that revoked on unmount would break the roster chip still
 * showing the same person, and would refetch S3 on every panel toggle. They are
 * released only by {@link clearProfilePicCache}.
 *
 * Cache key is `customerId|userId`, not `userId` alone: Unity downloads a
 * picture from the owner's NATURAL account while caching under the current
 * customer's folder, so the same user can legitimately resolve through
 * different accounts.
 */

const cache = new Map<string, string | null>();
const inflight = new Map<string, Promise<string | null>>();

/**
 * Fetch (or hit the cache for) one user's picture. Returns the `blob:` URL, or
 * `null` when the user has none — which is the common case and is what makes
 * callers fall back to initials.
 *
 * `naturalAccountId` is the account that OWNS the picture (Unity's
 * `LoadUserProfilePicAsync(userId, naturalAccountId)`); pass the viewing
 * customer when it is unknown, exactly as Unity does when it is empty
 * (`CustomerModel.cs:759-760`).
 *
 * **A negative result is cached too.** Most users have no picture, and without
 * this every re-render of a comment list would re-issue a doomed S3 GET per
 * author. Unity gets this for free by only ever calling once per session per
 * user behind its named lock.
 */
export async function loadProfilePic(
  naturalAccountId: string,
  userId: string,
  signal?: AbortSignal,
): Promise<string | null> {
  if (!naturalAccountId || !userId) return null;

  const key = `${naturalAccountId}|${userId}`;
  if (cache.has(key)) return cache.get(key) ?? null;

  const existing = inflight.get(key);
  if (existing) return existing;

  const storage = Ioc.resolve<IStorageModel>(STORAGE_MODEL);
  const promise = (async () => {
    const url = await storage.getUserProfilePicBlobUrl(naturalAccountId, userId, signal);
    cache.set(key, url);
    return url;
  })();

  inflight.set(key, promise);
  try {
    return await promise;
  } finally {
    inflight.delete(key);
  }
}

/** Synchronous peek — `undefined` when not yet resolved, `null` when known-absent. */
export function peekProfilePic(
  naturalAccountId: string,
  userId: string,
): string | null | undefined {
  return cache.get(`${naturalAccountId}|${userId}`);
}

/**
 * Drop every cached picture and revoke its `blob:` URL. Port of
 * `CustomerModel.ClearUserProfilePicCache` (`:705`) — call on sign-out and on
 * account switch, so one user's pictures never survive into another's session.
 * Returns the number of entries dropped, matching `clearAllThumbnails`.
 */
export function clearProfilePicCache(): number {
  let count = 0;
  for (const url of cache.values()) {
    if (url) {
      URL.revokeObjectURL(url);
      count++;
    }
  }
  cache.clear();
  return count;
}
